import { type CSSProperties, type ReactNode, useState } from 'react';
import { useClassTri } from '@/modules/tri/presentation/hooks/useClassTri';
import { useClassTriConfidence } from '@/modules/tri/presentation/hooks/use-class-tri-confidence';
import { useClassTriConfidenceStudents } from '@/modules/tri/presentation/hooks/use-class-tri-confidence-students';
import type {
  ClassTriConfidence,
  ClassTriConfidenceLevel,
} from '@/modules/tri/domain/entities/class-tri-confidence.entity';
import type { ClassTriConfidenceStudent } from '@/modules/tri/domain/entities/class-tri-confidence-student.entity';
import { downloadCsv } from './utils/csv';
import { slugify } from './shared';

const ENAMED_CONCEPTS: {
  label: string;
  min: number;
  max: number;
  conceito: number;
}[] = [
  { label: 'Menor que 40%', min: 0, max: 40, conceito: 1 },
  { label: '40% a 59,9%', min: 40, max: 60, conceito: 2 },
  { label: '60% a 74,9%', min: 60, max: 75, conceito: 3 },
  { label: '75% a 89,9%', min: 75, max: 90, conceito: 4 },
  { label: '90% ou mais', min: 90, max: Infinity, conceito: 5 },
];

const TRI_EXPLANATION_TEXT =
  'O cálculo utiliza a Teoria de Resposta ao Item (TRI), o mesmo método do ENAMED/ENARE: a nota considera a dificuldade e o poder de discriminação de cada questão, não apenas o número de acertos. O Conceito ENAMED (1 a 5) é definido a partir da faixa em que o percentual de alunos proficientes se encaixa, conforme a Tabela 1 da Nota Técnica 40/2025 (Inep/Daes).';

function HelpTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setOpen(false)}
        aria-label="O que é isso?"
        className="text-(--color-text-secondary) hover:border-(--color-accent) hover:text-(--color-accent) flex h-4 w-4 items-center justify-center rounded-full border border-current text-[10px] font-bold normal-case transition-colors"
      >
        ?
      </button>
      {open && (
        <div className="border-(--color-border) bg-(--color-bg-primary) text-(--color-text-secondary) absolute left-1/2 top-full z-10 mt-2 w-64 -translate-x-1/2 rounded-lg border p-3 text-xs font-normal normal-case leading-relaxed shadow-xl">
          {text}
        </div>
      )}
    </span>
  );
}

function EnamedGaugeCard({
  title,
  subtitle,
  score,
  scoreRangeLabel,
  activeLabel,
  showConceptTable = true,
  accentColor,
  confidenceLabel,
  editionId,
  editionTitle,
  confidence,
  hideScore = false,
  studentsCount,
  calculationPending = false,
}: {
  title: string;
  subtitle: string;
  score: number;
  scoreRangeLabel: string;
  activeLabel: string;
  showConceptTable?: boolean;
  unitLabel?: string;
  accentColor?: string;
  confidenceLabel?: string;
  editionId: string;
  editionTitle: string;
  confidence?: ClassTriConfidence;
  hideScore?: boolean;
  studentsCount?: number;
  calculationPending?: boolean;
}) {
  const activeConcept = hideScore
    ? undefined
    : ENAMED_CONCEPTS.find((c) => score >= c.min && score < c.max);
  const [showConfidenceInfo, setShowConfidenceInfo] = useState(false);

  return (
    <>
      <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border-2 backdrop-blur-sm">
        {/* Header */}
        <div className="border-(--color-border) flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-(--color-accent)/15 flex h-8 w-8 items-center justify-center rounded-lg">
              <svg
                className="text-(--color-accent) h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 3.741-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5"
                />
              </svg>
            </div>
            <div>
              <p className="text-(--color-accent) text-sm font-bold">{title}</p>
              <p className="text-(--color-text-secondary) mt-0.5 text-xs">
                {subtitle}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div
          className={
            showConceptTable ? 'grid grid-cols-1 md:grid-cols-2' : 'flex'
          }
        >
          {/* Left — gauge */}
          <div
            className={
              showConceptTable
                ? 'border-(--color-border) relative flex flex-1 flex-col items-center justify-center gap-4 border-b px-6 py-8 md:border-b-0 md:border-r'
                : 'relative flex flex-1 flex-wrap items-center justify-center gap-6 px-6 py-6'
            }
          >
            {calculationPending ? (
              <div className="border-(--color-border) bg-(--color-bg-primary)/60 absolute left-6 top-4 flex items-center gap-1.5 rounded-full border-2 px-3 py-1">
                <svg
                  className="text-(--color-text-secondary) h-4 w-4 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                  />
                </svg>
                <span className="text-(--color-text-secondary) text-xs font-extrabold uppercase tracking-wide">
                  Cálculo da projeção ENAMED ainda não realizado
                </span>
              </div>
            ) : (
              confidenceLabel && (
                <button
                  type="button"
                  onClick={() => setShowConfidenceInfo(true)}
                  aria-label="Sobre o nível de confiança"
                  style={
                    accentColor
                      ? ({ '--color-accent': accentColor } as CSSProperties)
                      : undefined
                  }
                  className="border-(--color-accent)/40 bg-(--color-accent)/10 hover:bg-(--color-accent)/20 absolute left-6 top-4 flex items-center gap-1.5 rounded-full border px-3 py-1 transition-colors"
                >
                  <svg
                    className="text-(--color-accent) h-4 w-4 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                    />
                  </svg>
                  <span className="text-(--color-accent) text-xs font-extrabold uppercase tracking-wide">
                    {confidenceLabel}
                  </span>
                </button>
              )
            )}
            <div className="border-(--color-accent) bg-(--color-accent)/10 flex h-32 w-32 items-center justify-center rounded-full border-4 shadow-[0_0_28px_color-mix(in_srgb,var(--color-accent)_20%,transparent)]">
              <span className="text-(--color-accent) text-6xl font-extrabold tabular-nums leading-none">
                {activeConcept?.conceito ?? '-'}
              </span>
            </div>
            {activeConcept && (
              <div className="border-(--color-accent)/40 bg-(--color-accent)/10 flex items-center gap-1.5 rounded-full border px-3 py-1">
                <span className="text-(--color-accent) text-xs font-bold uppercase tracking-wide">
                  Conceito ENAMED {activeConcept.conceito}
                </span>
              </div>
            )}
            {typeof studentsCount === 'number' && (
              <div className="border-(--color-border) bg-(--color-bg-primary)/60 text-(--color-text-secondary) absolute bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border px-2.5 py-1 text-[10px] font-medium">
                {studentsCount}{' '}
                {studentsCount === 1
                  ? 'aluno considerado'
                  : 'alunos considerados'}{' '}
                no cálculo
              </div>
            )}
          </div>

          {/* Right — concept table */}
          {showConceptTable && (
            <div className="flex flex-col gap-5 px-6 py-6">
              {/* Concept table */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <p className="text-(--color-text-secondary) text-xs font-semibold uppercase tracking-widest">
                    Conceito ENAMED
                  </p>
                  <HelpTooltip text={TRI_EXPLANATION_TEXT} />
                </div>
                <div className="border-(--color-border) overflow-hidden rounded-xl border">
                  <div className="border-(--color-border) flex items-center justify-between border-b px-3 py-1.5">
                    <span className="text-(--color-text-secondary) flex-1 text-xs font-medium">
                      {scoreRangeLabel}
                    </span>
                  </div>
                  {ENAMED_CONCEPTS.map((c) => {
                    const isActive = c.conceito === activeConcept?.conceito;
                    return (
                      <div
                        key={c.conceito}
                        className={[
                          'border-(--color-border) flex items-center gap-2.5 border-t px-3 py-2 transition-colors',
                          isActive ? 'bg-(--color-accent)/15' : '',
                        ].join(' ')}
                      >
                        <span
                          className={[
                            'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                            isActive
                              ? 'bg-(--color-accent) text-(--color-bg-primary)'
                              : 'bg-(--color-border) text-(--color-text-secondary)',
                          ].join(' ')}
                        >
                          {c.conceito}
                        </span>
                        <span
                          className={
                            isActive
                              ? 'text-(--color-accent) text-xs font-semibold'
                              : 'text-(--color-text-secondary) text-xs'
                          }
                        >
                          {c.label}
                          {isActive && (
                            <span className="text-(--color-text-secondary) ml-1.5 text-xs font-normal italic">
                              — {activeLabel}
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-(--color-text-secondary) text-[10px] italic leading-relaxed">
                  Faixas conforme Tabela 1 da Nota Técnica 40/2025 (Inep/Daes).
                </p>
              </div>
            </div>
          )}
        </div>
        {(confidenceLabel || calculationPending) && (
          <button
            type="button"
            onClick={
              calculationPending ? undefined : () => setShowConfidenceInfo(true)
            }
            disabled={calculationPending}
            className={
              calculationPending
                ? 'flex w-full cursor-not-allowed items-center gap-2.5 border-t border-slate-400/50 bg-slate-500/10 px-6 py-3 text-left opacity-60'
                : 'flex w-full items-center gap-2.5 border-t border-slate-400/50 bg-slate-500/20 px-6 py-3 text-left transition-colors hover:bg-slate-500/30'
            }
          >
            <svg
              className="h-4 w-4 shrink-0 text-slate-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
            <p className="flex-1 text-sm font-semibold text-slate-200">
              Entenda como é calculado esse índice de confiança
            </p>
            {!calculationPending && (
              <svg
                className="h-4 w-4 shrink-0 text-slate-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m8.25 4.5 7.5 7.5-7.5 7.5"
                />
              </svg>
            )}
          </button>
        )}
      </div>
      {showConfidenceInfo && (
        <ConfidenceInfoModal
          title={confidenceLabel}
          accentColor={accentColor}
          editionId={editionId}
          editionTitle={editionTitle}
          confidence={confidence}
          onClose={() => setShowConfidenceInfo(false)}
        />
      )}
    </>
  );
}

function AlertBox({
  children,
  detail,
  disabled = false,
  checked = false,
  onDownload,
  downloadDisabled = false,
}: {
  children: ReactNode;
  detail?: string;
  disabled?: boolean;
  checked?: boolean;
  onDownload?: () => void;
  downloadDisabled?: boolean;
}) {
  return (
    <div
      className={
        disabled
          ? 'flex items-center gap-2.5 rounded-xl border border-[#38bdf8]/40 bg-[#38bdf8]/10 px-4 py-3 opacity-60'
          : 'flex items-center gap-2.5 rounded-xl border border-[#38bdf8]/40 bg-[#38bdf8]/10 px-4 py-3'
      }
    >
      <svg
        className="h-5 w-5 shrink-0 text-white"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        {checked ? (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m4.5 12.75 6 6 9-13.5"
          />
        ) : (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        )}
      </svg>
      <div className="flex flex-1 flex-col gap-1.5">
        <p className="text-sm font-medium text-white">{children}</p>
        {detail && <p className="text-xs text-white opacity-80">{detail}</p>}
      </div>
      <button
        type="button"
        onClick={onDownload}
        disabled={!onDownload || downloadDisabled}
        className="flex shrink-0 items-center gap-1.5 rounded-lg border border-[#38bdf8] bg-[#38bdf8]/10 px-2.5 py-1.5 text-xs font-semibold text-[#38bdf8] transition-colors hover:bg-[#38bdf8]/20 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <svg
          className="h-3.5 w-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 12m0 0 4.5-4.5M12 12V3"
          />
        </svg>
        Baixar relatório
      </button>
    </div>
  );
}

function formatPercent(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}

function downloadParticipationReport(
  editionTitle: string,
  students: ClassTriConfidenceStudent[],
): void {
  downloadCsv(
    `${slugify(`participacao-${editionTitle}`)}.csv`,
    ['Nome', 'Email', 'Participou'],
    students.map((s) => [s.name, s.email, s.participated ? 'Sim' : 'Não']),
  );
}

function downloadCompletionReport(
  editionTitle: string,
  students: ClassTriConfidenceStudent[],
): void {
  downloadCsv(
    `${slugify(`conclusao-${editionTitle}`)}.csv`,
    [
      'Nome',
      'Email',
      'Questões respondidas',
      'Total de questões',
      '% concluído',
      'Atingiu 80%',
    ],
    students
      .filter((s) => s.participated)
      .map((s) => [
        s.name,
        s.email,
        s.answeredCount,
        s.totalQuestions,
        `${Math.round(s.completionRate * 100)}%`,
        s.completed80 ? 'Sim' : 'Não',
      ]),
  );
}

function ConfidenceInfoModal({
  title,
  accentColor,
  editionId,
  editionTitle,
  confidence,
  onClose,
}: {
  title?: string;
  accentColor?: string;
  editionId: string;
  editionTitle: string;
  confidence?: ClassTriConfidence;
  onClose: () => void;
}) {
  const participationMet = (confidence?.participationRate ?? 0) >= 0.7;
  const completionMet = (confidence?.completionRate ?? 0) >= 0.9;
  const { data: studentsData, isLoading: loadingStudents } =
    useClassTriConfidenceStudents(editionId);
  const students = studentsData?.students ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div
        className="border-(--color-border) bg-(--color-bg-primary) relative z-10 w-full max-w-xl rounded-2xl border p-6 shadow-2xl"
        style={
          accentColor
            ? ({ '--color-accent': accentColor } as CSSProperties)
            : undefined
        }
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <p className="text-(--color-text-secondary) text-xs font-semibold uppercase tracking-widest">
              Nível de confiança
            </p>
            <h3 className="text-(--color-accent) mt-1 text-lg font-bold">
              {title ?? 'Sobre esta projeção'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="text-(--color-text-secondary) hover:text-(--color-text-primary) shrink-0 transition-colors"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-3">
          {!confidence ? (
            <p className="text-(--color-text-secondary) text-sm leading-relaxed">
              Carregando...
            </p>
          ) : confidence.level === 'insuficientes' ? (
            <AlertBox
              downloadDisabled={loadingStudents}
              onDownload={() =>
                downloadCompletionReport(editionTitle, students)
              }
            >
              Você não atingiu o mínimo de 10 alunos com pelo menos 80% de
              conclusão do simulado.
            </AlertBox>
          ) : (
            <>
              <AlertBox
                disabled={participationMet}
                checked={participationMet}
                detail={`Atual: ${formatPercent(confidence.participationRate)} dos alunos`}
                downloadDisabled={loadingStudents}
                onDownload={() =>
                  downloadParticipationReport(editionTitle, students)
                }
              >
                Ter pelo menos 70% dos seus alunos realizando a prova.
              </AlertBox>
              <AlertBox
                disabled={completionMet}
                checked={completionMet}
                detail={`Atual: ${formatPercent(confidence.completionRate)} dos alunos`}
                downloadDisabled={loadingStudents}
                onDownload={() =>
                  downloadCompletionReport(editionTitle, students)
                }
              >
                Ter pelo menos 90% dos alunos que iniciaram a prova com pelo
                menos 80% do simulado concluído.
              </AlertBox>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const CONFIDENCE_META: Record<
  ClassTriConfidenceLevel,
  { label: string; color: string }
> = {
  alta: { label: 'Alta confiança', color: '#00e676' },
  media: { label: 'Média confiança', color: '#ffb900' },
  baixa: { label: 'Baixa confiança', color: '#ff1744' },
  insuficientes: { label: 'Dados insuficientes', color: '#9e9e9e' },
};

export function EnamedProjectionCard({
  editionId,
  editionTitle,
}: {
  editionId: string;
  editionTitle: string;
}) {
  const {
    data: classTri,
    isLoading: loadingTri,
    isError: errorTri,
  } = useClassTri(editionId);
  const {
    data: confidence,
    isLoading: loadingConfidence,
    isError: errorConfidence,
  } = useClassTriConfidence(editionId);

  if (loadingTri || loadingConfidence) {
    return <div className="h-64 animate-pulse rounded-2xl bg-white/5" />;
  }

  const calculationPending =
    errorTri ||
    errorConfidence ||
    !classTri ||
    !confidence ||
    classTri.percentProficient == null;
  const meta =
    calculationPending || !confidence
      ? undefined
      : CONFIDENCE_META[confidence.level];

  // Conceito Enade (Nota Técnica 40/2025, §7.2 + Tabela 1) é definido sobre o
  // PCPc (% de concluintes Proficiente) — não sobre a nota média (NF_DM).
  return (
    <div className="space-y-3">
      <EnamedGaugeCard
        title="Projeção ENAMED"
        subtitle="Estimativa baseada no desempenho atual da turma"
        score={classTri?.percentProficient ?? 0}
        scoreRangeLabel="% de alunos proficientes no simulado"
        activeLabel="sua instituição"
        accentColor={meta?.color}
        confidenceLabel={meta?.label}
        editionId={editionId}
        editionTitle={editionTitle}
        confidence={confidence}
        hideScore={calculationPending || confidence?.level === 'insuficientes'}
        studentsCount={
          calculationPending ? undefined : confidence?.studentsCount
        }
        calculationPending={calculationPending}
      />
    </div>
  );
}
